// Locale-aware display formatters — single source of truth for how the
// dashboard renders dates, times, and numbers. Hard-coded to Belgian
// conventions per `docs/superpowers/specs/2026-05-13-locale-be-design.md`:
//
//   - Dates use `en-BE`  — English language (month/weekday names stay
//                          "May" / "Mon", not "mei" / "ma"), Belgian
//                          day-month-year order, 24h time. Separator is
//                          a dash (`-`) instead of the locale-default
//                          slash so display dates read DD-MM-YYYY.
//   - Numbers use `nl-BE` — Belgian thousand `.` + decimal `,` (1.234,56).
//                          en-BE would give US-style `1,234.56`, which
//                          doesn't match Belgian invoices / ops sheets.
//
// API / DB values stay ISO 8601; this module is display-only.

export const LOCALE_DATE   = "en-BE";
export const LOCALE_NUMBER = "nl-BE";

// Intl.* constructor calls are expensive enough to justify memoising
// once at module load.
const dateFmt = new Intl.DateTimeFormat(LOCALE_DATE, {
  year: "numeric", month: "2-digit", day: "2-digit",
});
const timeFmt = new Intl.DateTimeFormat(LOCALE_DATE, {
  hour: "2-digit", minute: "2-digit", hour12: false,
});
const dateTimeFmt = new Intl.DateTimeFormat(LOCALE_DATE, {
  year: "numeric", month: "2-digit", day: "2-digit",
  hour: "2-digit", minute: "2-digit", hour12: false,
});
const monthYearFmt = new Intl.DateTimeFormat(LOCALE_DATE, {
  month: "long", year: "numeric",
});
const shortWeekdayFmt = new Intl.DateTimeFormat(LOCALE_DATE, { weekday: "short" });
const shortMonthFmt = new Intl.DateTimeFormat(LOCALE_DATE, { month: "short" });
const numberFmt = new Intl.NumberFormat(LOCALE_NUMBER);

const EMPTY = "—";

// Parse Date | string into a Date. Returns null when the input is
// nullish, empty, or unparseable. Date-only strings (YYYY-MM-DD) are
// parsed to LOCAL midnight, not UTC midnight, so a negative-timezone
// bundle wouldn't render the day before. (Belgium is UTC+1/+2 so this
// would never trigger in practice; the deliberate parse keeps the
// helper safe if the bundle ever runs elsewhere.)
function parseLooseDate(input: Date | string | null | undefined): Date | null {
  if (input == null || input === "") return null;
  if (input instanceof Date) return Number.isNaN(input.getTime()) ? null : input;
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(input);
  if (dateOnly) {
    return new Date(+dateOnly[1]!, +dateOnly[2]! - 1, +dateOnly[3]!);
  }
  const d = new Date(input);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatDate(input: Date | string | null | undefined): string {
  const d = parseLooseDate(input);
  return d ? dateFmt.format(d).replace(/\//g, "-") : EMPTY;
}

export function formatTime(input: Date | string | null | undefined): string {
  const d = parseLooseDate(input);
  return d ? timeFmt.format(d) : EMPTY;
}

export function formatDateTime(input: Date | string | null | undefined): string {
  const d = parseLooseDate(input);
  if (!d) return EMPTY;
  // Some browsers join with comma+space in dateTimeFmt; force a single
  // space for "DD-MM-YYYY HH:MM" output. Slashes from the en-BE locale
  // are swapped to dashes per the dash-separator convention.
  return dateTimeFmt.format(d).replace(/,\s+/, " ").replace(/\//g, "-");
}

export function formatMonthYear(d: Date): string {
  return monthYearFmt.format(d);
}

export function formatShortWeekday(d: Date): string {
  // Trim trailing "." that some locales append to short weekday names.
  return shortWeekdayFmt.format(d).replace(/\.$/, "");
}

export function formatShortMonth(d: Date): string {
  return shortMonthFmt.format(d).replace(/\.$/, "");
}

export function formatNumber(n: number, opts?: Intl.NumberFormatOptions): string {
  if (!opts) return numberFmt.format(n);
  return new Intl.NumberFormat(LOCALE_NUMBER, opts).format(n);
}
